import { useEffect, useState, type ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Sidebar } from '../Home/Sidebar';
import { ANALYTICS_BETA_ROUTE, ANALYTICS_PERFORMANCE_ROUTE, NAV_ROUTES } from '../Home/navRoutes';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { ANALYTICS } from './analyticsTokens';
import { fetchAnalytics, type AccessCensus, type AnalyticsSnapshot } from './analyticsApi';
import type { BetaAccessActions } from './betaAccessApi';
import { BETA_FEATURES } from './betaFeatures';
import { BetaFeaturesTab } from './BetaFeaturesTab';
import { PerformanceTab } from './PerformanceTab';
import { COUNTS, DEFAULT_COUNT, DEFAULT_RANGE, RANGES, type CountKey, type RangeKey } from './betaUsageRanges';
import type { ImpersonationCandidateList } from './impersonationApi';
import {
  ANALYTICS_WINDOWS,
  analyticsAccessTiles,
  analyticsHeadline,
  analyticsProblemRows,
  analyticsSections,
  formatComputedAt,
  formatUpdatedAgo,
  hasAnyEvents,
  type AccessTile,
  type AnalyticsCell,
  type AnalyticsRow,
  type AnalyticsSection,
  type HeadlineTile,
} from './analyticsTable';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export type AnalyticsTab = 'district' | 'beta' | 'performance';

type Props = {
  tab?: AnalyticsTab;
  source?: () => Promise<AnalyticsSnapshot>;
  rosterSource?: () => Promise<ImpersonationCandidateList>;
  accessActions?: BetaAccessActions;
  now?: () => Date;
};

const TABS: { key: AnalyticsTab; label: string; to: string }[] = [
  { key: 'district', label: 'District activity', to: NAV_ROUTES.analyticsAdmin },
  { key: 'beta', label: 'Beta features', to: ANALYTICS_BETA_ROUTE },
  { key: 'performance', label: 'Performance', to: ANALYTICS_PERFORMANCE_ROUTE },
];

const ALL_TIME = ANALYTICS_WINDOWS.length - 1;

const base = { fontFamily: ANALYTICS.font } as const;

const bodyText = {
  ...base,
  fontSize: 16,
  lineHeight: '24px',
  color: ANALYTICS.muted,
} as const;

const cardSx = {
  bgcolor: ANALYTICS.surface,
  borderRadius: '8px',
  border: `1px solid ${ANALYTICS.grey50}`,
} as const;

export function AdminAnalyticsPage({ tab = 'district', source = fetchAnalytics, rosterSource, accessActions, now = () => new Date() }: Props = {}) {
  const [snapshot, setSnapshot] = useState<AnalyticsSnapshot | null>(null);
  const [failed, setFailed] = useState(false);
  const [range, setRange] = useState<RangeKey>(DEFAULT_RANGE);
  const [count, setCount] = useState<CountKey>(DEFAULT_COUNT);
  // Bumped after a beta list change; the numbers already on screen stay until the new read lands.
  const [reads, setReads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setSnapshot(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source, reads]);

  const ready = !failed && snapshot !== null && snapshot.computed;

  return (
    <Box sx={{ display: 'flex', bgcolor: ANALYTICS.background, minHeight: HEIGHT_BELOW_BARS }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          gap: '32px',
          p: { xs: '40px 24px 56px', lg: '40px 100px 56px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '24px', flexWrap: 'wrap' }}>
            <PageHeader computedAt={ready ? snapshot.computedAt : undefined} now={now} />
            {tab === 'beta' && (
              <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Picker label="Count" testId="admin-analytics-count" options={COUNTS} value={count} onChange={setCount} />
                <Picker label="Time range" testId="admin-analytics-range" options={RANGES} value={range} onChange={setRange} />
              </Box>
            )}
          </Box>
          <Tabs active={tab} />
        </Box>

        {failed && (
          <Typography data-testid="admin-analytics-error" sx={bodyText}>
            Could not load analytics. Refresh to try again.
          </Typography>
        )}
        {!failed && snapshot === null && (
          <CircularProgress data-testid="admin-analytics-loading" size={24} />
        )}
        {!failed && snapshot !== null && tab === 'district' && <DistrictActivity snapshot={snapshot} />}
        {!failed && snapshot !== null && tab === 'performance' && <PerformanceTab snapshot={snapshot} now={now} />}
        {!failed && snapshot !== null && tab === 'beta' && (
          <BetaFeaturesTab
            snapshot={snapshot}
            range={range}
            count={count}
            rosterSource={rosterSource}
            accessActions={accessActions}
            onAccessChanged={() => setReads((n) => n + 1)}
            now={now}
          />
        )}
      </Box>
    </Box>
  );
}

function Tabs({ active }: { active: AnalyticsTab }) {
  return (
    <Box role="tablist" sx={{ display: 'flex', gap: '32px', borderBottom: `1px solid ${ANALYTICS.divider}` }}>
      {TABS.map(({ key, label, to }) => {
        const selected = key === active;
        return (
          <Box
            key={key}
            component={RouterLink}
            to={to}
            role="tab"
            aria-selected={selected}
            data-testid="admin-analytics-tab"
            sx={{
              ...base,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '0 0 12px',
              marginBottom: '-1px',
              fontSize: 16,
              lineHeight: '24px',
              fontWeight: selected ? 800 : 600,
              color: selected ? ANALYTICS.primary : ANALYTICS.muted,
              textDecoration: 'none',
              borderBottom: `2px solid ${selected ? ANALYTICS.primary : 'transparent'}`,
              '&:hover': { color: selected ? ANALYTICS.primary : ANALYTICS.ink },
            }}
          >
            {label}
            {key === 'beta' && (
              <Box
                component="span"
                sx={{ padding: '0 8px', borderRadius: '9999px', bgcolor: ANALYTICS.indigo50, fontSize: 12, lineHeight: '20px', fontWeight: 700, color: ANALYTICS.primary }}
              >
                {BETA_FEATURES.length} features
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

type PickerProps<K extends string> = {
  label: string;
  testId: string;
  options: readonly { key: K; button: string }[];
  value: K;
  onChange: (key: K) => void;
};

function Picker<K extends string>({ label, testId, options, value, onChange }: PickerProps<K>) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', gap: '4px', padding: '4px', borderRadius: '8px', bgcolor: ANALYTICS.grey50 }}>
      {options.map((option) => {
        const pressed = option.key === value;
        return (
          <Box
            key={option.key}
            component="button"
            type="button"
            aria-pressed={pressed}
            data-testid={testId}
            onClick={() => onChange(option.key)}
            sx={{
              ...base,
              border: 0,
              cursor: 'pointer',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: 16,
              lineHeight: '24px',
              fontWeight: pressed ? 700 : 600,
              color: pressed ? ANALYTICS.ink : ANALYTICS.muted,
              bgcolor: pressed ? ANALYTICS.surface : 'transparent',
              boxShadow: pressed ? ANALYTICS.elevationCard : 'none',
              '&:hover': { color: ANALYTICS.ink, bgcolor: pressed ? ANALYTICS.surface : ANALYTICS.actionHover },
              '&:focus-visible': { outline: `2px solid ${ANALYTICS.primary}`, outlineOffset: '1px' },
            }}
          >
            {option.button}
          </Box>
        );
      })}
    </Box>
  );
}

function DistrictActivity({ snapshot }: { snapshot: AnalyticsSnapshot }) {
  const populated = snapshot.computed && hasAnyEvents(snapshot.windows);
  return (
    <>
      <ScopeNotes />
      {snapshot.computed && <AccessStrip access={snapshot.access} />}
      {!snapshot.computed && (
        <Typography data-testid="admin-analytics-uncounted" sx={bodyText}>
          The counts have not been computed yet. They are refreshed every 20 minutes.
        </Typography>
      )}
      {snapshot.computed && !populated && (
        <Typography data-testid="admin-analytics-empty" sx={bodyText}>
          No events have been recorded yet.
        </Typography>
      )}
      {populated && (
        <>
          <HeadlineStrip tiles={analyticsHeadline(snapshot.windows)} />
          <LaneScroller>
            <ActivityTable sections={analyticsSections(snapshot.windows)} />
            <ProblemsCard rows={analyticsProblemRows(snapshot.windows)} />
          </LaneScroller>
        </>
      )}
    </>
  );
}

/**
 * Both cards share one horizontal scroller. Scrolled separately their lanes
 * drift out of step, and the problems card's figures lose the window headers
 * that sit above them in the activity table.
 */
function LaneScroller({ children }: { children: ReactNode }) {
  return (
    <Box data-testid="admin-analytics-lane-scroller" sx={{ overflowX: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          // Widen to the longest row so every row's borders run the full
          // scrolled width instead of stopping at the viewport.
          minWidth: 'max-content',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function PageHeader({ computedAt, now }: { computedAt?: string; now: () => Date }) {
  // The "Updated N minutes ago" line is computed at render, so a page left
  // open would otherwise freeze on whatever it said when it loaded.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!computedAt) return undefined;
    const id = window.setInterval(() => tick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [computedAt]);
  return (
    <Box>
      <Typography
        component="h1"
        sx={{
          ...base,
          fontSize: 32,
          lineHeight: '40px',
          fontWeight: 800,
          letterSpacing: ANALYTICS.tracking.tight,
          color: ANALYTICS.ink,
        }}
      >
        Analytics
      </Typography>
      {computedAt && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '4px' }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              flexShrink: 0,
              borderRadius: '9999px',
              bgcolor: ANALYTICS.success,
            }}
          />
          <Typography
            data-testid="admin-analytics-computed-at"
            title={formatComputedAt(computedAt)}
            sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: ANALYTICS.muted }}
          >
            {formatUpdatedAgo(computedAt, now())}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ScopeNotes() {
  return (
    <Box sx={{ mt: '-16px' }}>
      <Typography
        data-testid="admin-analytics-scope"
        sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.disabled }}
      >
        District users only — nothing Flashlight staff did is counted here
      </Typography>
      {/* The rollup keys a person by whatever the event carried: their user id
          once identity resolution succeeded, their Cognito sub before that. One
          human can hold both, so the figures are a ceiling and the spec says
          not to present them as exact. */}
      <Typography
        data-testid="admin-analytics-estimate"
        sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.disabled }}
      >
        People counts are a close estimate — one person can be counted twice
      </Typography>
    </Box>
  );
}

const stripHeading = {
  ...base,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 700,
  letterSpacing: ANALYTICS.tracking.wider,
  textTransform: 'uppercase',
  color: ANALYTICS.muted,
} as const;

function AccessStrip({ access }: { access?: AccessCensus }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', pb: '32px', borderBottom: `1px solid ${ANALYTICS.divider}` }}>
      <Typography sx={stripHeading}>Who has Insight access</Typography>
      {!access && (
        <Typography data-testid="admin-analytics-access-unavailable" sx={bodyText}>
          Access counts are not available yet. They are refreshed with the rest of the page every 20 minutes.
        </Typography>
      )}
      {access && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '24px' }}>
            {analyticsAccessTiles(access).map((tile) => (
              <AccessTileCard key={tile.key} tile={tile} />
            ))}
          </Box>
          {access.byDistrict.length > 0 && <AccessByDistrict rows={access.byDistrict} />}
        </>
      )}
    </Box>
  );
}

function AccessTileCard({ tile }: { tile: AccessTile }) {
  return (
    <Box data-testid="admin-analytics-access-tile" sx={{ ...cardSx, display: 'flex', flexDirection: 'column', flex: 1, gap: '6px', p: '24px' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <Typography
          sx={{
            ...base,
            fontSize: 56,
            lineHeight: '56px',
            fontWeight: 800,
            letterSpacing: ANALYTICS.tracking.tight,
            fontVariantNumeric: 'tabular-nums',
            color: ANALYTICS.ink,
          }}
        >
          {tile.figure}
        </Typography>
        <Typography sx={{ ...base, fontSize: 20, lineHeight: '28px', color: ANALYTICS.muted }}>of {tile.of}</Typography>
      </Box>
      <Typography sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink, minHeight: 48 }}>{tile.label}</Typography>
      {tile.caption && (
        <Typography sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>{tile.caption}</Typography>
      )}
    </Box>
  );
}

const accessCell = { ...base, fontSize: 14, lineHeight: '20px', fontVariantNumeric: 'tabular-nums', textAlign: 'right', width: 140, flexShrink: 0 } as const;

function AccessByDistrict({ rows }: { rows: AccessCensus['byDistrict'] }) {
  return (
    <Box data-testid="admin-analytics-access-by-district" role="table" aria-label="Districts with Insight access" sx={{ ...cardSx, p: '20px 24px 8px' }}>
      <Box role="row" sx={{ display: 'flex', alignItems: 'flex-end', gap: '24px', pb: '12px', borderBottom: `2px solid ${ANALYTICS.ink}` }}>
        <Typography role="columnheader" component="h2" sx={{ ...base, flex: 1, fontSize: 20, lineHeight: '28px', fontWeight: 700, letterSpacing: ANALYTICS.tracking.tight, color: ANALYTICS.ink }}>
          Districts with access
        </Typography>
        <Typography role="columnheader" sx={{ ...accessCell, fontSize: 12, lineHeight: '16px', fontWeight: 700, color: ANALYTICS.muted }}>
          Admins granted
        </Typography>
        <Typography role="columnheader" sx={{ ...accessCell, fontSize: 12, lineHeight: '16px', fontWeight: 700, color: ANALYTICS.muted }}>
          Teachers granted
        </Typography>
      </Box>
      {rows.map((row) => (
        <Box
          key={row.districtId}
          role="row"
          data-testid="admin-analytics-access-district"
          sx={{ display: 'flex', alignItems: 'center', gap: '24px', py: '12px', borderBottom: `1px solid ${ANALYTICS.grey50}`, '&:last-of-type': { borderBottom: 0 } }}
        >
          <Typography role="cell" sx={{ ...base, flex: 1, fontSize: 16, lineHeight: '24px', fontWeight: 600, color: ANALYTICS.ink }}>
            {row.districtName || row.districtId}
          </Typography>
          <Typography role="cell" sx={{ ...accessCell, color: ANALYTICS.ink }}>
            {row.admins.granted} of {row.admins.total}
          </Typography>
          <Typography role="cell" sx={{ ...accessCell, color: ANALYTICS.ink }}>
            {row.teachers.granted} of {row.teachers.total}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function HeadlineStrip({ tiles }: { tiles: HeadlineTile[] }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        pb: '32px',
        borderBottom: `1px solid ${ANALYTICS.divider}`,
      }}
    >
      <Typography
        sx={{
          ...base,
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 700,
          letterSpacing: ANALYTICS.tracking.wider,
          textTransform: 'uppercase',
          color: ANALYTICS.muted,
        }}
      >
        In the last 30 days
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '24px' }}>
        {tiles.map((tile) => (
          <Tile key={tile.key} tile={tile} />
        ))}
      </Box>
    </Box>
  );
}

function Tile({ tile }: { tile: HeadlineTile }) {
  return (
    <Box
      data-testid="admin-analytics-tile"
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column', flex: 1, gap: '6px', p: '24px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <Typography
          sx={{
            ...base,
            fontSize: 56,
            lineHeight: '56px',
            fontWeight: 800,
            letterSpacing: ANALYTICS.tracking.tight,
            fontVariantNumeric: 'tabular-nums',
            color: tile.figure === null ? ANALYTICS.grey100 : ANALYTICS.ink,
          }}
        >
          {tile.figure ?? '—'}
        </Typography>
        {tile.ofThose !== null && (
          <Typography sx={{ ...base, fontSize: 20, lineHeight: '28px', color: ANALYTICS.muted }}>
            of those {tile.ofThose}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          ...base,
          fontSize: 16,
          lineHeight: '24px',
          fontWeight: 700,
          color: ANALYTICS.ink,
          // Two lines' worth, so the captions share a baseline across all
          // three tiles however long the labels run.
          minHeight: 48,
        }}
      >
        {tile.label}
      </Typography>
      {tile.caption && (
        <Typography sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>
          {tile.caption}
        </Typography>
      )}
    </Box>
  );
}

function LaneHeader({ title }: { title: string }) {
  return (
    <Box
      role="row"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: `${ANALYTICS.lane.gap}px`,
        pb: '12px',
        borderBottom: `2px solid ${ANALYTICS.ink}`,
      }}
    >
      <Box role="columnheader" sx={{ flex: 1 }}>
        <Typography
          component="h2"
          sx={{
            ...base,
            fontSize: 20,
            lineHeight: '28px',
            fontWeight: 700,
            letterSpacing: ANALYTICS.tracking.tight,
            color: ANALYTICS.ink,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', width: ANALYTICS.lane.block, flexShrink: 0 }}>
        {ANALYTICS_WINDOWS.map(({ key, label }) => (
          <Box
            key={key}
            role="columnheader"
            sx={{ display: 'flex', width: ANALYTICS.lane.cell, flexShrink: 0, justifyContent: 'flex-end' }}
          >
            <Typography
              sx={{
                ...base,
                fontSize: 12,
                lineHeight: '16px',
                fontWeight: 700,
                textAlign: 'right',
                color: ANALYTICS.muted,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ActivityTable({ sections }: { sections: AnalyticsSection[] }) {
  return (
    <Box
      data-testid="admin-analytics-activity"
      role="table"
      aria-label="What people did"
      sx={{ ...cardSx, p: '24px 24px 8px' }}
    >
      <LaneHeader title="What people did" />
      {sections.map((section) => (
        <Box key={section.title} role="rowgroup" sx={{ pt: '20px' }}>
          <Typography
            data-testid="admin-analytics-section"
            sx={{
              ...base,
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 800,
              letterSpacing: ANALYTICS.tracking.wider,
              textTransform: 'uppercase',
              color: ANALYTICS.primary,
              pb: '8px',
            }}
          >
            {section.title}
          </Typography>
          {section.rows.map((row) => (
            <RowLine key={row.key} row={row} testId="admin-analytics-row" />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function ProblemsCard({ rows }: { rows: AnalyticsRow[] }) {
  return (
    <Box
      data-testid="admin-analytics-problems"
      sx={{
        bgcolor: ANALYTICS.surface,
        borderRadius: '8px',
        border: `1px solid ${ANALYTICS.red100}`,
        p: '20px 24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pb: '12px',
        }}
      >
        <AlertIcon />
        <Typography
          component="h2"
          sx={{
            ...base,
            fontSize: 20,
            lineHeight: '28px',
            fontWeight: 700,
            letterSpacing: ANALYTICS.tracking.tight,
            color: ANALYTICS.ink,
          }}
        >
          Where people got stuck
        </Typography>
      </Box>
      <Box role="table" aria-label="Where people got stuck">
        {rows.map((row) => (
          <RowLine
            key={row.key}
            row={row}
            testId="admin-analytics-problem-row"
          // This card exists to be noticed, but only for the windows a reader
          // can still act on. All time stays recessive like everywhere else.
            emphasise={(index, cell) =>
              row.key === 'access.denied' && (index === 1 || index === 2) && cell.count > 0
            }
          />
        ))}
      </Box>
    </Box>
  );
}

function AlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle
        cx="9"
        cy="9"
        r="7.25"
        fill="none"
        stroke={ANALYTICS.red600}
        strokeWidth="1.5"
      />
      <path
        d="M9 5.25v4.5M9 12.25v.5"
        fill="none"
        stroke={ANALYTICS.red600}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

type RowProps = {
  row: AnalyticsRow;
  testId: string;
  emphasise?: (index: number, cell: AnalyticsCell) => boolean;
};

function RowLine({ row, testId, emphasise }: RowProps) {
  return (
    <Box
      data-testid={testId}
      role="row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${ANALYTICS.lane.gap}px`,
        py: '12px',
        borderTop: `1px solid ${ANALYTICS.divider}`,
      }}
    >
      {/* No minWidth:0 here on purpose. The flex default of min-content is
          exactly the room the unwrapped label and its chip need, so the lane
          sizes itself and the scroller never lets it collapse under them. */}
      <Box role="rowheader" sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '2px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography
            sx={{
              ...base,
              fontSize: 16,
              lineHeight: '24px',
              fontWeight: 700,
              color: ANALYTICS.ink,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {row.label}
          </Typography>
          {row.neverFired && <NeverHappenedChip tone={row.tone} />}
        </Box>
        {row.caption && (
          <Typography sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>
            {row.caption}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', width: ANALYTICS.lane.block, flexShrink: 0 }}>
        {row.cells.map((cell, index) => (
          <Cell
            key={ANALYTICS_WINDOWS[index]?.key}
            windowLabel={ANALYTICS_WINDOWS[index]?.label ?? ''}
            cell={cell}
            recessive={index === ALL_TIME}
            zeroTone={index === ALL_TIME ? row.tone : 'grey'}
            emphasised={emphasise?.(index, cell) ?? false}
          />
        ))}
      </Box>
    </Box>
  );
}

function NeverHappenedChip({ tone }: { tone: AnalyticsRow['tone'] }) {
  const yellow = tone === 'yellow';
  return (
    <Box
      data-testid="admin-analytics-chip"
      data-tone={tone}
      sx={{
        display: 'flex',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        p: '2px 7px',
        borderRadius: '4px',
        bgcolor: yellow ? ANALYTICS.yellow100 : ANALYTICS.grey50,
      }}
    >
      <Typography
        sx={{
          ...base,
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 700,
          color: yellow ? ANALYTICS.yellow900 : ANALYTICS.grey600,
        }}
      >
        Never happened
      </Typography>
    </Box>
  );
}

type CellProps = {
  cell: AnalyticsCell;
  windowLabel: string;
  recessive: boolean;
  zeroTone: AnalyticsRow['tone'];
  emphasised: boolean;
};

function readCell(cell: AnalyticsCell, windowLabel: string): string {
  if (cell.count === 0) return `${windowLabel}: none`;
  const times = `${cell.count} ${cell.count === 1 ? 'time' : 'times'}`;
  if (cell.people === undefined) return `${windowLabel}: ${times}`;
  return `${windowLabel}: ${cell.people} ${cell.people === 1 ? 'person' : 'people'}, ${times}`;
}

function Cell({ cell, windowLabel, recessive, zeroTone, emphasised }: CellProps) {
  const figureSx = {
    ...base,
    fontSize: 20,
    lineHeight: '24px',
    fontVariantNumeric: 'tabular-nums',
  } as const;
  const timesCarriesTheFigure = emphasised && cell.people === undefined;

  return (
    <Box
      data-testid="admin-analytics-cell"
      role="cell"
      // The figures are split across two lines and the problems card has no
      // header row of its own, so each cell names its own window.
      aria-label={readCell(cell, windowLabel)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        width: ANALYTICS.lane.cell,
        flexShrink: 0,
      }}
    >
      {cell.count === 0 ? (
        // "0 people / 0 times" is the same nothing twice, so a zero is one mark.
        <Typography
          sx={{
            ...figureSx,
            fontWeight: zeroTone === 'yellow' ? 800 : 500,
            color: zeroTone === 'yellow' ? ANALYTICS.yellow900 : ANALYTICS.grey100,
          }}
        >
          0
        </Typography>
      ) : (
        <>
          {cell.people !== undefined && (
            // Figure and unit are inline so they sit on one baseline and the
            // cell reads as "5 people" to a screen reader and to the clipboard.
            <Typography sx={{ ...figureSx, textAlign: 'right' }}>
              <Box
                component="span"
                {...(emphasised ? { 'data-emphasis': 'red' } : {})}
                sx={{
                  fontWeight: emphasised ? 800 : recessive ? 500 : 700,
                  color: emphasised
                    ? ANALYTICS.red600
                    : recessive
                      ? ANALYTICS.muted
                      : ANALYTICS.ink,
                }}
              >
                {cell.people}
              </Box>{' '}
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  lineHeight: '16px',
                  fontWeight: 500,
                  color: recessive ? ANALYTICS.disabled : ANALYTICS.muted,
                }}
              >
                {cell.people === 1 ? 'person' : 'people'}
              </Box>
            </Typography>
          )}
          <Typography
            {...(timesCarriesTheFigure ? { 'data-emphasis': 'red' } : {})}
            sx={{
              ...base,
              fontSize: 12,
              lineHeight: '16px',
              // With no people line the times line is the only figure there
              // is, so the card's emphasis has to land on it instead.
              fontWeight: timesCarriesTheFigure ? 800 : 400,
              color: timesCarriesTheFigure
                ? ANALYTICS.red600
                : recessive
                  ? ANALYTICS.disabled
                  : ANALYTICS.muted,
            }}
          >
            {cell.count} {cell.count === 1 ? 'time' : 'times'}
          </Typography>
        </>
      )}
    </Box>
  );
}
